import { Link } from '@tanstack/react-router';

const districtColors = [
  '#FEF3C7', // Light yellow
  '#ECFCCB', // Light green
  '#FFEDD5', // Light orange
  '#DCFCE7', // Mint green
  '#E2E8F0', // Blue gray
  '#E0E7FF', // Light blue
  '#FFE4E6', // Light pink
  '#F3E8FF', // Light purple
];

export interface DistrictColumnProps {
  orgId?: string;
  orgName: string;
  shouldHaveInitial?: boolean;
  shouldHaveLink?: boolean;
  testId?: string;
}

/**
 * Assigns a consistent color based on the district name.
 * The same district name will always get the same color.
 *
 * @param districtName - The district name
 * @returns A hexadecimal color as string
 */
export function getDistrictColor(districtName: string): string {
  // Normalize the name (lowercase, no extra spaces)
  const normalizedName = districtName.toLowerCase().trim();

  // Create a more robust hash
  let hash = 0;
  for (let i = 0; i < normalizedName.length; i++) {
    const char = normalizedName.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }

  // Use the hash to select a color
  const index = Math.abs(hash) % districtColors.length;
  return districtColors[index];
}

/**
 * Gets all available colors for districts
 * @returns Array of hexadecimal colors
 */
export function getAvailableDistrictColors(): string[] {
  return [...districtColors];
}

/**
 * Renders a district column with optional link and initial.
 * @param props - District column properties
 * @returns JSX element representing the district column
 */
export function getDistrictColumn({
  orgId,
  orgName,
  shouldHaveInitial = true,
  shouldHaveLink = true,
  testId,
}: DistrictColumnProps) {
  const backgroundColor = getDistrictColor(orgName);

  return (
    <div className="flex items-center gap-x-2">
      {shouldHaveInitial && (
        <span
          className="text-xs rounded-full text-center w-8 h-8 shrink-0 flex items-center justify-center"
          style={{ backgroundColor }}
        >
          {orgName.charAt(0).toUpperCase()}
        </span>
      )}
      {shouldHaveLink && orgId ? (
        <Link to={'/orgs/$orgId'} params={{ orgId }} className="text-blue-600" data-testid={testId}>
          {orgName}
        </Link>
      ) : (
        <span className="text-blue-600" data-testid={testId}>
          {orgName}
        </span>
      )}
    </div>
  );
}
